import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { IndicatorValue, Topic } from '@shapeable/gesda-types';
import { CellLabel, ContentEntityIntro, ContentNode, DottedLine, EntityGrid, Grid, LightMode, NextPageLink, PersonBioCell, useIndicatorGroups, useView, VideoImage } from '@shapeable/ui';
import { transformEvent, transformTopicExpertsByEdition } from '../../utils/transforms';
import { TopicView } from '../../types';
import { sortBy, compact } from 'lodash';
import { GptLayout } from '@shapeable/gpt';
import { TopicAnticipatoryImpactLayout } from './topic-anticipatory-impact-layout';
import { anticipatoryPotentialIndicatorValues, subTopicAnchorId, TopicAnticipationPotentialChart } from '@shapeable/gesda-ui';
import { PersonCard } from './person-card';
import { useCurrentEditionMembers } from '../../hooks/use-current-edition-members';
import { filterEntitiesWithLinkedSite } from '../../utils/filter-entities-with-linked-site';
import { SubTopicsListLayout } from './sub-topics-list-layout';
import { TopicPspIndicatorGroups } from './topic-psp-indicator-groups';
const cls = classNames('topic-view-layout');

// -------- Types -------->

// -------- Props -------->

export type TopicViewLayoutProps = Classable & HasChildren & { 
  entity?: Topic;
  showView?: (view: TopicView) => boolean;
};

export const TopicViewLayoutDefaultProps: Omit<TopicViewLayoutProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(4)};
  `,
});

const SectionStyles = breakpoints({
  base: css`
    margin-bottom: ${theme.UNIT(5)};
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(5)};
  `,
});

const GptLayoutStyles = breakpoints({
  base: css`
    .shp--gpt-prompt {
      &:first-child {
        border-top: none;
      }
    };
  `,
});

const ContentLabelStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('dark')};
    font-size: 1em;
    font-weight: 400;
    margin-bottom: ${theme.UNIT(1)};
  `,
});

const IntroStyles = breakpoints({
  base: css`
    padding-top: ${theme.UNIT(0)};
    font-size: 1em;
  `,
});

const VideosStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(4)};
  `,
  desktopLarge: css`
    flex-direction: row;
  `,
});

const VideoStyles = breakpoints({
  base: css`
    
  `,
});

const ContentStyles = breakpoints({
  base: css`
    font-weight: 300;
  `,
});

const ConnectionsStyles = breakpoints({
  base: css`
    .shp--card {
      background-color: ${theme.COLOR('bright')};
    }
  `,
});

const ChartStyles = breakpoints({
  base: css`
    background: ${theme.COLOR('bright-warm')};
    padding: ${theme.UNIT(4)};
    color: ${theme.COLOR('text')};
  `,
  desktop: css`
    padding: ${theme.UNIT(5)};
    margin-right: 0;
    margin-left: 0;
  `,
});

const AnticipatoryImpactLayoutStyles = breakpoints({
  base: css`
    
  `,
});

const PspIndicatorGroupsStyles = breakpoints({
  base: css`
    width: 100%;
    flex-shrink: 0;
  `,
});

const ResourcesStyles = breakpoints({
  base: css`
    
  `,
});

const GlobalExpertsStyles = breakpoints({
  base: css`
    
  `,
});

const GlobalExpertStyles = breakpoints({
  base: css`
    .shp--badge {
      padding: 0 ${theme.UNIT(2)};
    }
  `,
});

const NextLinkStyles = breakpoints({
  base: css`
    margin: ${theme.UNIT(4)} 0;
  `,
});

const SubTopicsListStyles = breakpoints({
  base: css`
    margin-top: ${theme.UNIT(10)};
  `,
});

const SubTopicJumpLinksStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(0.5)};
    padding: 0;
  `,
});

const SubTopicJumpLinkStyles = breakpoints({
  base: css`
    font-size: 1em;
    list-style-type: none;
    color: ${theme.COLOR('subtle')};
    font-weight: 400;

    a {
      color: inherit;
      display: inline-block;
      isolation: isolate;
      overflow: visible;
      padding: 0;
      position: relative;
      transition: color 180ms ease, transform 180ms ease;
    }

    a::before {
      background-color: var(--topic-color, ${theme.COLOR('primary')});
      bottom: 0;
      content: '';
      left: -${theme.UNIT(1)};
      position: absolute;
      right: -${theme.UNIT(1)};
      top: 0;
      transform: scaleX(0);
      transform-origin: left center;
      transition: transform 220ms ease;
      text-decoration: none;
      border-radius: ${theme.UNIT(1)};
      z-index: -1;
    }

    a:hover,
    a:focus-visible {
      color: ${theme.COLOR('bright')};
      text-decoration: none;
      transform: translateX(${theme.UNIT(1)});
    }

    a:hover::before,
    a:focus-visible::before {
      transform: scaleX(1);
    }
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,

  Section: styled.section`${SectionStyles}`,
      GptLayout: styled(GptLayout)`${GptLayoutStyles}`,

      ContentLabel: styled(CellLabel)`${ContentLabelStyles}`,
      Intro: styled(ContentEntityIntro)`${IntroStyles}`,
      SubTopicJumpLinks: styled.ul`${SubTopicJumpLinksStyles}`,
        SubTopicJumpLink: styled.li`${SubTopicJumpLinkStyles}`,
      Videos: styled.div`${VideosStyles}`,
      Video: styled(VideoImage)`${VideoStyles}`,
      Content: styled(ContentNode)`${ContentStyles}`,
      Chart: styled(TopicAnticipationPotentialChart)`${ChartStyles}`,
      AnticipatoryImpactLayout: styled(TopicAnticipatoryImpactLayout)`${AnticipatoryImpactLayoutStyles}`,
        PspIndicatorGroups: styled(TopicPspIndicatorGroups)`${PspIndicatorGroupsStyles}`,

      Connections: styled(EntityGrid)`${ConnectionsStyles}`,
  
      SubTopicsList: styled(SubTopicsListLayout)`${SubTopicsListStyles}`,

      Resources: styled(EntityGrid)`${ResourcesStyles}`,

      GlobalExperts: styled(EntityGrid)`${GlobalExpertsStyles}`,
       GlobalExpert: styled(PersonCard)`${GlobalExpertStyles}`,

      NextLink: styled(NextPageLink)`${NextLinkStyles}`,
};

export const TopicViewLayout: Shapeable.FC<TopicViewLayoutProps> = (props) => {
  const { className, children, entity, showView } = props;

  const { description, connectedPages = [], committeeMembers = [], anticipatoryImpact, 
    anticipatoryImpactImage, citations = [], feedEntries = [], subTopics = [], 
    videos = [], events = [], indicatorValues = [], trend } = entity;
  const indicatorGroups = useIndicatorGroups();

    const hasvideos = !!videos.length;
    const hasIndicatorValues = anticipatoryPotentialIndicatorValues(indicatorValues).length > 0;
    const hasSubtopicIndicatorValues = subTopics.some(subTopic => anticipatoryPotentialIndicatorValues(subTopic.indicatorValues || []).length > 0);

    const filteredConnectionsBySite = filterEntitiesWithLinkedSite(connectedPages);

    const transformedEvents = events.map(event => transformEvent(event));
    const connections = compact([...filteredConnectionsBySite, trend, ...transformedEvents]);

    const resources = sortBy([...citations, ...feedEntries], 'date');
  
    // Have made changes to useCurrentEditionMembers to filter by editions not the badge.
    // Karmen added 2025 edition which causes issues with filtering by the badge field as some members are 2025,
    // so they wont appear even if they have 2025 set too (want to only show 2025 members).
    const filteredByEdition = useCurrentEditionMembers(committeeMembers, entity);

    // Very quick fix to ensure badges show 2025
    const filteredByCurrentEdition = transformTopicExpertsByEdition(filteredByEdition);

    const deriveValue: (iv: IndicatorValue) => number = 
    (iv) => {
      return (iv.numericValue - 0.41357) * 1000 / 5.8 + 18;
    };
  
    const hasPspIndicatorGroups = indicatorGroups.some((group) => (
      group.layout?.component === 'IndicatorGroupLayoutPspChart' &&
      (group.indicators || []).some((indicator) => (
        indicatorValues.some((indicatorValue) => indicatorValue.indicator?.id === indicator.id)
      ))
    ));

    const hasAnticipatoryImpact = 
      anticipatoryImpact && anticipatoryImpact.text && (
      hasPspIndicatorGroups ||
      anticipatoryImpactImage && anticipatoryImpactImage.image && anticipatoryImpactImage.image.url
      );

    const subTopicJumpLinkStyle = {
      '--topic-color': entity.color?.value,
    } as React.CSSProperties;

  return (
    <My.Container className={cls.name(className)}>
      {
        showView('overview') && 
        <My.Section>
          {/* <My.ContentLabel>{'Summary'}</My.ContentLabel> */}
          <My.Intro entity={entity} />
          <My.SubTopicJumpLinks style={subTopicJumpLinkStyle}>
            <My.ContentLabel>{'Sub-Topics'}</My.ContentLabel>
            {
              subTopics.map((subTopic) => (
                <My.SubTopicJumpLink key={subTopic.id}>
                  <a href={`#${subTopicAnchorId(subTopic)}`}>
                    {subTopic.name}
                  </a>
                </My.SubTopicJumpLink>
              ))
            }
          </My.SubTopicJumpLinks>
          {
            hasvideos && (
              <My.Connections
                items={videos}
                spacing={4}
              />
            )
          }
          <My.Content entity={description} />
          {
            (hasIndicatorValues || hasSubtopicIndicatorValues) && (
              <LightMode>
                <My.Chart entity={entity} deriveValue={deriveValue} upperValue={80} />
              </LightMode>
            )
          }
          {
            hasPspIndicatorGroups ? (
              <My.AnticipatoryImpactLayout entity={entity}>
                <My.PspIndicatorGroups entity={entity} />
              </My.AnticipatoryImpactLayout>
            ) : hasAnticipatoryImpact && (
              <My.AnticipatoryImpactLayout entity={entity} />
            )
          }
          <My.SubTopicsList items={subTopics} />
        </My.Section>
      }
      {
        showView('connections') && 
        <My.Section>
          <My.Connections items={connections} spacing={4} />
        </My.Section>
      }
      {
        showView('resources') && 
        <My.Section>
          <My.Resources items={resources} spacing={4} />
        </My.Section>
      }
      {
        showView('contributors') && 
        <My.Section>
          <My.GlobalExperts 
            spacing={4} 
            tabletAutoColumns={3} 
            desktopAutoColumns={4} 
            items={filteredByCurrentEdition} />
        </My.Section>
      }
      <DottedLine height={2} />
      <My.NextLink />
    </My.Container>
  )
};

TopicViewLayout.defaultProps = TopicViewLayoutDefaultProps;
TopicViewLayout.cls = cls;